import { useCallback, useEffect, useMemo, useState } from "react";
import { VideoSource } from "../../Enums/enums";
import { isCategoryMissing } from "../../Enums/platform_constants";
import { flattenItems } from "./batchItems";
import { isCopyOutdated, savedEditOf } from "./CopyEditControl";

// Tab status. "saved" and "applied" come from the card itself — a
// renderPayload, and `batchApplied` when Apply to all wrote it and nobody has
// opened and saved that tab since. "unsaved" and "error" are marks this
// session puts on a tab while it is open.
export const BatchStatus = {
  IDLE: "idle",
  SAVED: "saved",
  APPLIED: "applied",
  // Reuses another platform's video (Step 3's Duplicate): nothing to edit.
  DUPLICATE: "duplicate",
  UNSAVED: "unsaved",
  // Saved, but it holds a copy of another platform's edit that was saved
  // differently since (user request 2026-10-07): amber until copied again.
  OUTDATED: "outdated",
  ERROR: "error",
};

/** A tab that needs nothing more from the user: saved, or a duplicate. */
export const isDoneStatus = (s) => s === BatchStatus.SAVED || s === BatchStatus.DUPLICATE;

/** True when the tab's platform requires a category and none is chosen (Step 3's rule). */
export const needsCategory = (item) => isCategoryMissing(item.platform, item.config);

const cardStatus = (config) => {
  if (config?.source === VideoSource.DUPLICATE) return BatchStatus.DUPLICATE;
  if (!config?.renderPayload) return BatchStatus.IDLE;
  return config.batchApplied ? BatchStatus.APPLIED : BatchStatus.SAVED;
};
/** Keys of saved tabs whose copied edit is older than its source's saved edit. */
const outdatedKeys = (items) => {
  const out = new Set();
  for (const item of items) {
    const doc = savedEditOf(item.config);
    if (!doc) continue;
    const siblings = items.filter((i) => i.videoKey === item.videoKey && i.key !== item.key);
    if (isCopyOutdated(doc, siblings)) out.add(item.key);
  }
  return out;
};
const statusFrom = (item, marks, outdated) =>
  marks[item.key] || (outdated.has(item.key) ? BatchStatus.OUTDATED : cardStatus(item.config));

/** Where to land when the editor opens: the first tab still to edit or review. */
const firstToEdit = (items) => items.find((i) => !isDoneStatus(cardStatus(i.config))) || items[0] || null;

/**
 * The batch editor's navigation and status state over `groups` (see
 * buildBatchGroups). Only the active tab is ever "live"; every other tab is
 * just its card's saved config.
 */
export default function useBatchSession(open, groups) {
  const items = useMemo(() => flattenItems(groups), [groups]);
  const [activeKey, setActiveKey] = useState(null);
  const [marks, setMarks] = useState({});

  // Opening lands on the first tab still to edit; closing forgets this
  // session's marks (saved edits live on the cards, not here).
  useEffect(() => {
    if (!open) {
      setActiveKey(null);
      setMarks({});
      return;
    }
    setActiveKey((key) => (key && items.some((i) => i.key === key) ? key : firstToEdit(items)?.key ?? null));
  }, [open, items]);

  const outdated = useMemo(() => outdatedKeys(items), [items]);
  const statusOf = useCallback((item) => statusFrom(item, marks, outdated), [marks, outdated]);

  const activeIndex = items.findIndex((i) => i.key === activeKey);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
  const activeVideo = useMemo(() => {
    if (!activeItem) return null;
    for (const g of groups) {
      const v = g.videos.find((vid) => vid.key === activeItem.videoKey);
      if (v) return v;
    }
    return null;
  }, [groups, activeItem]);

  // Duplicates count as done: they are finished as soon as their source is.
  const savedCount = items.filter((i) => isDoneStatus(statusOf(i))).length;
  const appliedCount = items.filter((i) => statusOf(i) === BatchStatus.APPLIED).length;
  const categoryMissingCount = items.filter(needsCategory).length;

  /** Switch video, staying on the same platform when that video has it. */
  const selectVideo = useCallback(
    (videoKey) => {
      const videoItems = items.filter((i) => i.videoKey === videoKey);
      if (videoItems.length === 0) return;
      const samePlatform = activeItem && videoItems.find((i) => i.platform === activeItem.platform);
      setActiveKey((samePlatform || firstToEdit(videoItems)).key);
    },
    [items, activeItem],
  );

  const selectPlatform = useCallback(
    (platform) => {
      const next = activeItem && items.find((i) => i.videoKey === activeItem.videoKey && i.platform === platform);
      if (next) setActiveKey(next.key);
    },
    [items, activeItem],
  );

  const goTo = useCallback((key) => setActiveKey(key), []);
  const goPrev = useCallback(() => activeIndex > 0 && setActiveKey(items[activeIndex - 1].key), [items, activeIndex]);
  const goNext = useCallback(
    () => activeIndex >= 0 && activeIndex < items.length - 1 && setActiveKey(items[activeIndex + 1].key),
    [items, activeIndex],
  );

  /** Put (`"unsaved"`/`"error"`) or clear (`null`) a session mark on a tab. */
  const setMark = useCallback((key, mark) => {
    setMarks((prev) => {
      if ((prev[key] || null) === mark) return prev;
      const next = { ...prev };
      if (mark) next[key] = mark;
      else delete next[key];
      return next;
    });
  }, []);

  /** A new edit on a tab: amber, unless its last save failed — that stays red until it saves. */
  const markUnsaved = useCallback((key) => {
    setMarks((prev) => (prev[key] ? prev : { ...prev, [key]: BatchStatus.UNSAVED }));
  }, []);

  return {
    items,
    markUnsaved,
    activeItem,
    activeVideo,
    activeIndex,
    savedCount,
    appliedCount,
    categoryMissingCount,
    statusOf,
    selectVideo,
    selectPlatform,
    goTo,
    goPrev,
    goNext,
    setMark,
  };
}
